import { useEffect, useRef } from "react";
import { useNowTick } from "../hooks/useNowTick";
import type { FleetStats } from "../api/types";

/** The fleet search. Its query is the vehicle list's own filter, lifted into
 *  Layout, so typing here narrows the list beside the map. */
export interface TopBarSearch {
  value: string;
  onChange: (query: string) => void;
  /** Enter: select the first match and show it on the map. */
  onSubmit: () => void;
  /** How many buses match the query; null when there is no query, or the
   *  fleet has not loaded enough to say. */
  matches: number | null;
}

interface TopBarProps {
  /** Same object MapStatsOverlay renders from — one source, so the two
   *  on-duty counts on screen can never disagree. */
  fleetStats: FleetStats;
  operator: { name: string; initials: string; role: string };
  /** False while the live state is not known yet — the counters are then
   *  structurally zero and must not read as a real count. */
  statsKnown?: boolean;
  search: TopBarSearch;
  /** Active alerts, for the bell's badge. */
  alertCount: number;
  /** Any of them severe: the badge turns red. */
  alertSevere?: boolean;
  onOpenAlerts: () => void;
}

const IS_MAC = typeof navigator !== "undefined" && /Mac|iPhone|iPad/i.test(navigator.platform || navigator.userAgent);

export default function TopBar({ fleetStats, operator, statsKnown = true, search, alertCount, alertSevere = false, onOpenAlerts }: TopBarProps) {
  const now = useNowTick(1000);
  const d = new Date(now);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");

  // Short timezone name (e.g. "PKT", "EST")
  const tz = (() => {
    try {
      const parts = new Intl.DateTimeFormat(undefined, { timeZoneName: "short" }).formatToParts(d);
      return parts.find((p) => p.type === "timeZoneName")?.value ?? "";
    } catch {
      return "";
    }
  })();

  // ⌘K / Ctrl+K focuses the search from anywhere in the console — the hint
  // has been printed in the box all along with nothing listening for it.
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // ⌘K on a Mac, Ctrl+K elsewhere. On a Mac, Ctrl+K is the system's
      // delete-to-end-of-line in every text field, so it is left alone.
      const mod = IS_MAC ? e.metaKey && !e.ctrlKey : e.ctrlKey && !e.metaKey;
      if (!mod || e.altKey || e.shiftKey || e.key.toLowerCase() !== "k") return;
      e.preventDefault();
      inputRef.current?.focus();
      inputRef.current?.select();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const querying = search.value.trim() !== "" && search.matches != null;
  const noMatch = querying && search.matches === 0;

  return (
    <header className="occ-topbar">
      {/* Search */}
      <div className={"tb-search" + (noMatch ? " no-match" : "")}>
        <svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="7" />
          <path d="M21 21l-4.35-4.35" />
        </svg>
        <input
          ref={inputRef}
          type="text"
          placeholder="Search bus, driver or trip"
          aria-label="Search bus, driver or trip"
          value={search.value}
          onChange={(e) => search.onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              search.onSubmit();
            } else if (e.key === "Escape") {
              search.onChange("");
              e.currentTarget.blur();
            }
          }}
        />
        {querying ? (
          <span className="tb-search-count mono" aria-live="polite">
            {noMatch ? "no match" : `${search.matches} found · ↵`}
          </span>
        ) : (
          <span className="tb-kbd">{IS_MAC ? "⌘K" : "Ctrl K"}</span>
        )}
      </div>

      <div className="tb-spacer" />

      {/* Fleet on-duty count — same FleetStats object MapStatsOverlay uses */}
      <div className="tb-fleet-stat">
        <span className="dot" />
        <span className="label">ON DUTY</span>
        <span className="value mono">{statsKnown ? `${fleetStats.onDuty}/${fleetStats.total}` : "—/—"}</span>
      </div>

      {/* Clock */}
      <div className="tb-clock">
        <span className="t">{hh}:{mm}:{ss}</span>
        <span className="tz">{tz}</span>
      </div>

      {/* Notifications: the active alerts. */}
      <button
        className="tb-bell"
        type="button"
        onClick={onOpenAlerts}
        title="Alerts"
        aria-label={alertCount > 0 ? `Alerts: ${alertCount} active` : "Alerts"}
      >
        <svg viewBox="0 0 24 24" width={18} height={18} fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 3a7 7 0 0 0-7 7c0 5-3 6-3 6h20s-3-1-3-6a7 7 0 0 0-7-7z" />
          <path d="M10 21a2 2 0 0 0 4 0" />
        </svg>
        {alertCount > 0 && (
          <span className={"tb-bell-badge mono" + (alertSevere ? " crit" : "")}>{alertCount > 99 ? "99+" : alertCount}</span>
        )}
      </button>

      {/* User profile */}
      <div className="tb-user">
        <div className="av">{operator.initials}</div>
        <div className="info">
          <span className="name">{operator.name}</span>
          <span className="email">{operator.role}</span>
        </div>
      </div>
    </header>
  );
}
